/**
 * Helpers for toGlobalID and fromGlobalId
 */
import { isObject, transform } from 'lodash';

import { base64, isBase64ID, unbase64 } from '../utils/base64';

// eslint-disable-next-line @typescript-eslint/naming-convention
interface IResolvedGlobalId {
  type: string;
  id: string;
}

/**
 * Takes a type name and an ID specific to that type name, and returns a
 * "global ID" that is unique among all types.
 */
export function toGlobalId(type: string, id: string): string {
  return base64([type, id].join(':'));
}

/**
 * Takes the "global ID" created by toGlobalID, and returns the type name and ID
 * used to create it.
 */
export function fromGlobalId(globalId: string): IResolvedGlobalId {
  const unbasedGlobalId = unbase64(globalId);
  const delimiterPos = unbasedGlobalId.indexOf(':');
  return {
    id: unbasedGlobalId.substring(delimiterPos + 1),
    type: unbasedGlobalId.substring(0, delimiterPos),
  };
}

/**
 * resolve global ID to local ID and check its type
 */
export const toLocalID = (globalID: string, expectedType: string, validate = true): string => {
  const { type, id } = fromGlobalId(globalID);
  if (type !== expectedType && validate) {
    throw new Error(`ID resolves to incorrect object. Expected ${expectedType}, got ${type}`);
  }
  return id;
};

export const fromLocalID = (id: string, type: string): string => toGlobalId(type, id);

/**
 * Safe parse string to local ID when possible
 * If strict is true, it will throw error if value is not a valid local ID or global ID
 * @example 123 => 123
 * @example VXNlcjoxMjY1 => 1265
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toLocalIDSafe = (value: any, strict = false) => {
  if (value === String(parseInt(value))) {
    return value;
  }
  if (typeof value === 'string' && isBase64ID(value)) {
    return fromGlobalId(value).id;
  }
  if (strict) {
    throw new Error(`Value '${value}' is not a valid local ID or global ID`);
  }
  return value;
};

export const toLocalIDIfNeeded = (unknownID: string, expectedType: string, validate = true): string =>
  unknownID ? (Number(unknownID) ? String(unknownID) : toLocalID(unknownID, expectedType, validate)) : unknownID;

/**
 * If key is ID like field which ends with ID or IDs
 */
export const isIDField = (key: string) =>
  ['id', 'ids'].includes(key) || String(key).endsWith('ID') || String(key).endsWith('IDs');

/**
 * Transform ID related fields to local ID
 * @internal
 */
const parseID = <T>(obj: T, paths: string[], doParse = false, prePath = ''): T => {
  if (!obj) {
    return obj;
  }

  if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
    return toLocalIDSafe(obj);
  }

  return transform(obj as Record<string, unknown>, (result, value, key) => {
    let newValue = value;
    const currentPath = `${prePath ? `${prePath}.` : ''}${key}`;

    if (isIDField(key)) {
      result[key] = parseID(value, paths, true, currentPath);
      return;
    }

    const path = `${prePath}.${key}`;
    const cleanPath = path.replace(/\.\d+?/g, '');
    if (paths.includes(cleanPath)) {
      result[key] = parseID(value, paths, true, currentPath);
      return;
    }

    if (isObject(value) && !(value instanceof Date)) {
      result[key] = parseID(value, paths, doParse, currentPath);
      return;
    }

    if (doParse) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        newValue = toLocalIDSafe(value);
      }
    }
    result[key] = newValue;
  });
};

/**
 * Transform all ID fields to local ID
 */
export const toLocalIDFilter = <T>(value: T, paths: string[] = []) => {
  return parseID(value, paths);
};

type FreeformID = string | number | null;

/**
 * Compare two IDs by explicitly casting to strings
 */
export const areEqualIDs = (ID1: FreeformID, ID2: FreeformID): boolean => `${ID1}` === `${ID2}`;

export interface EdgeInterface<T> {
  cursor?: string;
  node: T;
}

export interface ConnectionInterface<T> {
  totalCount?: string;
  edges: Array<EdgeInterface<T>>;
}
